import React, { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Remarkable } from 'remarkable';
import { linkify } from 'remarkable/linkify';

import AnalyticsService from '../../../services/AnalyticsService';
import { chatReply } from '../../../slices/LeoSlice';
import { AnalyticsEventEnum, AppDispatch, RootState } from '../../../types';
import { LEO_HYPER_PROMPT_LINK_PATTERN } from '../../../utils/LeoUtils';

interface MarkdownProps {
  content: string;
  msgId: string;
}

const md = new Remarkable().use(linkify);

md.set({
  html: true,
  breaks: true,
  typographer: true,
  linkTarget: '_blank',
});

const Markdown: React.FC<MarkdownProps> = ({ content, msgId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const session = useSelector((state: RootState) => state.leo.session);

  const onHyperPromptClick = useCallback(
    async (e: Event) => {
      e.preventDefault();
      // @ts-ignore
      const prompt = e.target?.innerText;
      await dispatch(
        chatReply(
          session?.id!,
          prompt,
          false,
          {
            prompt,
            title: prompt,
            category: null,
            prompt_type: 'HYPER_PROMPT',
          },
          undefined,
          location.pathname.slice(1),
        ),
      );
    },
    [dispatch, location.pathname, session?.id],
  );

  const handleNormalLinkClick = (event: Event) => {
    const href = (event.target as HTMLAnchorElement).getAttribute('href');
    AnalyticsService.instance().logEvent(
      AnalyticsEventEnum.LEO_RESPONSE_LINK_CLICK,
      { link: href },
    );
  };

  const parsedMarkdown = useMemo(() => md.render(content), [content]);

  useEffect(() => {
    const links = document.querySelectorAll(`#leo-markdown-${msgId} a`);
    links.forEach((link) => {
      const hrefAttr = link.getAttribute('href');
      if (hrefAttr && hrefAttr.startsWith(LEO_HYPER_PROMPT_LINK_PATTERN)) {
        link.classList.add('hyper-prompt');
        link.addEventListener('click', onHyperPromptClick);
      } else {
        link.classList.add('chat-link');
        link.addEventListener('click', handleNormalLinkClick);
      }
    });

    // Cleanup event listeners
    return () => {
      links.forEach((link) => {
        link.removeEventListener('click', onHyperPromptClick);
        link.removeEventListener('click', handleNormalLinkClick);
      });
    };
  }, [content, msgId, onHyperPromptClick]);

  return (
    <div
      id={`leo-markdown-${msgId}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: parsedMarkdown }}
    />
  );
};

export default Markdown;
